import React, { useState, useMemo, useEffect, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useAuth } from '../contexts/AuthContext';
import { Link } from 'react-router-dom';
import api from '../config/api';
import { FaChartBar, FaFilter, FaPrint, FaList, FaSearch, FaFileAlt, FaFileExcel, FaFileCsv, FaFilePdf, FaChevronDown, FaTimes, FaSave, FaSort, FaSortUp, FaSortDown, FaClock } from 'react-icons/fa';
import { toast } from 'react-toastify';
import FicheDetailLink from '../components/FicheDetailLink';
import RendementHoraireQualif from '../components/RendementHoraireQualif';
import { exportToCSV, exportToExcel, exportToPDF } from '../utils/exportUtils';
import SystemMessageBanner from '../components/SystemMessageBanner';
import { getFicheRowByEtatClassName } from '../utils/etatColorContrast';
import { getTodayLocal, toDateTimeLocalValue, splitDateTimeLocalValue, formatDateTimeFr } from '../utils/dateUtils';
import { toggleSortConfig, sortRowsByConfig } from '../utils/tableSort';
import {
  FicheRowIndexHeader,
  FicheRowIndexCell,
  getFicheRowNumber,
} from '../components/fiches/FicheRowIndexColumn';
import './ProductionQualif.css';
import useForceDesktopViewport from '../hooks/useForceDesktopViewport';

const ID_ETAT_HC = 55;

const getFicheEtatDisplay = (fiche) => {
  if (fiche.ko === 1 || fiche.ko === '1') {
    return { label: 'KO', color: '#dc3545' };
  }
  if (Number(fiche.id_etat_final) === ID_ETAT_HC) {
    return { label: fiche.etat_titre || 'HC', color: fiche.etat_color || '#ff9800' };
  }
  const isGroupe0 = fiche.etat_groupe === '0' || fiche.etat_groupe === 0;
  if (isGroupe0) {
    return { label: fiche.etat_titre || '', color: fiche.etat_color || '#ccc' };
  }
  return { label: 'Validée', color: '#4CAF50' };
};

const formatDelta = (value, suffix = '') => {
  const n = Number(value) || 0;
  if (n > 0) return { text: `+${n}${suffix}`, className: 'delta-positive' };
  if (n < 0) return { text: `${n}${suffix}`, className: 'delta-negative' };
  return { text: `0${suffix}`, className: 'delta-neutral' };
};

const formatPeriodRange = (period) => {
  if (!period) return '—';
  const start = period.start_datetime
    ? formatDateTimeFr(period.start_datetime)
    : `${period.date_debut || ''} ${(period.time_debut || '').slice(0, 5)}`.trim();
  const end = period.end_datetime
    ? formatDateTimeFr(period.end_datetime)
    : `${period.date_fin || ''} ${(period.time_fin || '').slice(0, 5)}`.trim();
  return `${start} → ${end}`;
};

const ProductionQualif = () => {
  useForceDesktopViewport('production-qualif-page');
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [showFilters, setShowFilters] = useState(true);
  const [viewMode, setViewMode] = useState('stats'); // 'stats' (RP) ou 'fiches' (RP + superviseur)
  const [searchTerm, setSearchTerm] = useState('');
  const [isMultiSelectOpen, setIsMultiSelectOpen] = useState(false);
  const multiSelectRef = useRef(null);

  const [filters, setFilters] = useState({
    date_debut: getTodayLocal(),
    date_fin: getTodayLocal(),
    time_debut: '00:00',
    time_fin: '23:59',
    id_rp: '',
    id_superviseur: '',
    id_etat_final: [] // Tableau pour multi-select
  });

  const [sortConfig, setSortConfig] = useState({ key: 'superviseur', direction: 'asc' });

  // État pour gérer l'édition du commentaire qualité
  const [editingComment, setEditingComment] = useState({ hash: null, value: '' });

  // Vérifier si l'utilisateur est un RP Qualification (fonction 12) ou Superviseur Qualification (fonction 2)
  const isRPQualif = user?.fonction === 12;
  const isSuperviseurQualif = user?.fonction === 2;
  const isAdmin = user?.fonction === 1;
  const isBackoffice = Number(user?.fonction) === 11;
  const isBackofficeOrAdmin = isBackoffice || isAdmin;
  const canViewStats = isRPQualif || isSuperviseurQualif || isBackofficeOrAdmin;
  const canViewFiches = isRPQualif || isSuperviseurQualif || isBackofficeOrAdmin;
  const canViewRendementHoraire = isBackoffice;
  const canSeeCommentaireQualite = isRPQualif || isSuperviseurQualif || isBackofficeOrAdmin;
  
  // Vérifier si l'utilisateur peut modifier/créer des commentaires qualité
  // Seul Admin (fonction 1) peut modifier
  const canEditCommentaireQualite = isAdmin;
  const canFilterByRp = isBackofficeOrAdmin;

  useEffect(() => {
    setSortConfig({
      key: viewMode === 'fiches' ? 'agent_pseudo' : 'superviseur',
      direction: 'asc',
    });
  }, [viewMode]);

  const { data: rpsData } = useQuery(
    'rps-qualif-production',
    async () => {
      const res = await api.get('/management/utilisateurs');
      const rps = res.data.data?.filter((u) => Number(u.fonction) === 12 && Number(u.etat) > 0) || [];
      return [...rps].sort((a, b) =>
        String(a.pseudo || '').localeCompare(String(b.pseudo || ''), 'fr', { sensitivity: 'base' })
      );
    },
    { enabled: canFilterByRp && !!user }
  );

  // Récupérer les superviseurs (périmètre selon le rôle)
  const { data: superviseursData } = useQuery(
    'superviseurs-assignes-rp-production',
    async () => {
      const res = await api.get('/management/utilisateurs');
      if (isRPQualif) {
        // Pour RP Qualification : seulement les superviseurs assignés
        return res.data.data?.filter(u => u.id_rp_qualif === user?.id && u.etat > 0) || [];
      } else {
        // Pour les autres : tous les superviseurs (utilisateurs avec agents)
        return res.data.data?.filter(u => {
          // Un superviseur est quelqu'un qui a des agents sous sa responsabilité
          const hasAgents = res.data.data?.some(agent => 
            agent.chef_equipe === u.id && agent.fonction === 3 && agent.etat > 0
          );
          return hasAgents && u.etat > 0 && u.fonction !== 3;
        }) || [];
      }
    },
    { enabled: !!user }
  );

  // Récupérer les états - uniquement groupe 0 + Validé pour RP Qualification
  const { data: etatsData } = useQuery('etats-production-qualif', async () => {
    const res = await api.get('/management/etats');
    let etats = res.data.data || [];
    
    // Pour RP Qualification, filtrer uniquement les états groupe 0
    if (isRPQualif) {
      etats = etats.filter(e => e.groupe === '0' || e.groupe === 0);
    }
    
    return etats;
  });

  // Récupérer les statistiques de production
  const { data: statsData, isLoading: loadingStats } = useQuery(
    ['production-qualif', filters],
    async () => {
      const params = {};
      if (filters.date_debut) params.date_debut = filters.date_debut;
      if (filters.date_fin) params.date_fin = filters.date_fin;
      if (filters.time_debut) params.time_debut = filters.time_debut;
      if (filters.time_fin) params.time_fin = filters.time_fin;
      if (filters.id_rp) params.id_rp = filters.id_rp;
      if (filters.id_superviseur) params.id_superviseur = filters.id_superviseur;
      // Pour les statistiques, on peut envoyer le premier état ou filtrer côté backend
      if (filters.id_etat_final && filters.id_etat_final.length > 0) {
        // Si un seul état, l'envoyer directement
        if (filters.id_etat_final.length === 1) {
          params.id_etat_final = filters.id_etat_final[0];
        }
        // Sinon, on ne filtre pas côté backend pour les stats (on affiche tout)
      }
      
      const res = await api.get('/statistiques/production-qualif', { params });
      return res.data.data;
    },
    { enabled: viewMode === 'stats' && canViewStats }
  );

  // Récupérer les agents pour le filtre (si nécessaire)
  const { data: agentsData } = useQuery(
    'agents-production-qualif',
    async () => {
      const res = await api.get('/management/utilisateurs');
      return res.data.data?.filter(u => u.fonction === 3 && u.etat > 0) || [];
    },
    { enabled: viewMode === 'fiches' && !!user }
  );

  // Récupérer les fiches créées par les agents des superviseurs assignés au RP
  // La route /fiches/agents-sous-responsabilite gère déjà le filtrage pour les RP Qualification
  const { data: fichesData, isLoading: loadingFiches, error: fichesError } = useQuery(
    ['fiches-production-qualif', filters, viewMode],
    async () => {
      const params = {
        page: 1,
        production_qualif: '1',
      };
      if (filters.date_debut) params.date_debut = filters.date_debut;
      if (filters.date_fin) params.date_fin = filters.date_fin;
      if (filters.time_debut) params.time_debut = filters.time_debut;
      if (filters.time_fin) params.time_fin = filters.time_fin;
      if (filters.id_rp) params.id_rp = filters.id_rp;
      // Filtrer par superviseur côté backend
      if (filters.id_superviseur) {
        params.id_superviseur = filters.id_superviseur;
      }
      // Filtrer par état (y compris "validated")
      // Envoyer tous les états au backend pour un filtrage optimisé
      if (filters.id_etat_final && filters.id_etat_final.length > 0) {
        // Envoyer le tableau d'états au backend
        params.id_etat_final = filters.id_etat_final;
      }
      
      try {
        const res = await api.get('/fiches/agents-sous-responsabilite', { params });
        return res.data;
      } catch (error) {
        console.error('Erreur lors de la récupération des fiches:', error);
        throw error;
      }
    },
    {
      enabled: viewMode === 'fiches' && canViewFiches,
      retry: 1
    }
  );

  // Mutation pour mettre à jour le commentaire qualité
  const updateCommentaireQualiteMutation = useMutation(
    async ({ hash, commentaire_qualite }) => {
      const res = await api.patch(`/fiches/${hash}/field`, {
        field: 'commentaire_qualite',
        value: commentaire_qualite
      });
      return res.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['fiches-production-qualif']);
        toast.success('Commentaire qualité enregistré avec succès');
        setEditingComment({ hash: null, value: '' });
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Erreur lors de l\'enregistrement du commentaire');
      }
    }
  );

  const handleSaveComment = (hash) => {
    const commentaire = editingComment.hash === hash ? editingComment.value : '';
    updateCommentaireQualiteMutation.mutate({ hash, commentaire_qualite: commentaire });
  };

  const handleKeyDown = (e, hash) => {
    if (e.ctrlKey && e.key === 'Enter') {
      handleSaveComment(hash);
    } else if (e.key === 'Escape') {
      setEditingComment({ hash: null, value: '' });
    }
  };

  // Filtrer les fiches par recherche rapide et par états multiples
  const filteredFiches = useMemo(() => {
    if (!fichesData?.data) return [];
    
    let filtered = fichesData.data;
    
    // Filtrer par états multiples
    if (filters.id_etat_final && filters.id_etat_final.length > 0) {
      filtered = filtered.filter((fiche) => {
        const isGroupe0 = fiche.etat_groupe === '0' || fiche.etat_groupe === 0;
        const ficheIdEtat = String(fiche.id_etat_final);
        const isFicheKo = fiche.ko === 1 || fiche.ko === '1';
        const isFicheHc = Number(fiche.id_etat_final) === ID_ETAT_HC;

        const isValidatedSelected = filters.id_etat_final.includes('validated');
        const isKoSelected = filters.id_etat_final.includes('ko');
        const isHcSelected = filters.id_etat_final.includes('hc');
        const isFicheValidated = !isGroupe0 && !isFicheKo;

        const isEtatSelected = filters.id_etat_final.includes(ficheIdEtat);

        return (
          (isValidatedSelected && isFicheValidated)
          || (isKoSelected && isFicheKo)
          || (isHcSelected && isFicheHc)
          || (isEtatSelected && !isFicheKo)
        );
      });
    }
    
    // Filtrer par recherche rapide
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(fiche => {
        return (
          (fiche.nom && fiche.nom.toLowerCase().includes(term)) ||
          (fiche.prenom && fiche.prenom.toLowerCase().includes(term)) ||
          (fiche.tel && fiche.tel.includes(term)) ||
          (fiche.cp && fiche.cp.includes(term)) ||
          (fiche.agent_pseudo && fiche.agent_pseudo.toLowerCase().includes(term)) ||
          (fiche.etat_titre && fiche.etat_titre.toLowerCase().includes(term)) ||
          (fiche.commentaire_qualite && fiche.commentaire_qualite.toLowerCase().includes(term))
        );
      });
    }
    
    return filtered;
  }, [fichesData, searchTerm, filters.id_etat_final]);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const handleDateTimeFilterChange = (bound, value) => {
    const { date, time } = splitDateTimeLocalValue(value);
    if (bound === 'debut') {
      setFilters((prev) => ({ ...prev, date_debut: date, time_debut: time }));
    } else {
      setFilters((prev) => ({ ...prev, date_fin: date, time_fin: time }));
    }
  };

  const handleSort = (key) => {
    setSortConfig((prev) => toggleSortConfig(prev, key));
  };

  const renderSortIcon = (key) => {
    if (!key || sortConfig.key !== key) {
      return <FaSort className="sort-icon" />;
    }
    return sortConfig.direction === 'asc'
      ? <FaSortUp className="sort-icon sort-active" />
      : <FaSortDown className="sort-icon sort-active" />;
  };

  // Fermer le dropdown multi-select quand on clique en dehors
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (multiSelectRef.current && !multiSelectRef.current.contains(event.target)) {
        setIsMultiSelectOpen(false);
      }
    };

    if (isMultiSelectOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isMultiSelectOpen]);

  const handlePrint = () => {
    window.print();
  };

  // Fonctions d'export
  const handleExportCSV = () => {
    if (viewMode === 'fiches' && fiches.length > 0) {
      // Préparer les données avec le superviseur
      const exportData = fiches.map(fiche => {
        const agent = agentsData?.find(a => a.id === fiche.id_agent);
        const superviseur = superviseurs.find(s => s.id === agent?.chef_equipe);
        const superviseurName = superviseur?.pseudo || '-';
        
        const { label: displayEtat } = getFicheEtatDisplay(fiche);
        
        return {
          ...fiche,
          superviseur_name: superviseurName,
          etat_titre: displayEtat
        };
      });
      
      const columns = [
        { key: 'date_insert_time', label: 'Date création' },
        { key: 'agent_pseudo', label: 'Agent' },
        { key: 'nom', label: 'Nom' },
        { key: 'prenom', label: 'Prénom' },
        { key: 'tel', label: 'Téléphone' },
        { key: 'cp', label: 'CP' },
        { key: 'etat_titre', label: 'État' },
        ...(canSeeCommentaireQualite ? [{ key: 'commentaire_qualite', label: 'Commentaire Qualité' }] : [])
      ];
      exportToCSV(exportData, columns, 'production-qualif-fiches');
    } else if (viewMode === 'stats' && stats.superviseurs && stats.superviseurs.length > 0) {
      // Exporter les statistiques en format tableau croisé
      // Colonnes : Superviseur + tous les états
      const etatLabels = stats.etats.map(etat => etat.titre || etat.abbreviation);
      
      // Créer les colonnes
      const columns = [
        { key: 'superviseur', label: 'Superviseur' },
        ...etatLabels.map(label => ({ key: `etat_${label}`, label })),
        { key: 'total', label: 'Total' },
        { key: 'etat_Validé', label: 'Validé' }
      ];
      
      // Créer les lignes (une par superviseur)
      const statsData = stats.superviseurs.map(superviseurStat => {
        const superviseurName = superviseurStat.superviseur?.pseudo || 'N/A';
        
        const row = { superviseur: superviseurName };
        
        // Ajouter les valeurs pour chaque état
        stats.etats.forEach(etat => {
          const label = etat.titre || etat.abbreviation;
          row[`etat_${label}`] = superviseurStat.stats[etat.id]?.count || 0;
        });

        row.total = superviseurStat.total || 0;
        // Ajouter la valeur "Validé"
        row['etat_Validé'] = superviseurStat.stats['validated']?.count || 0;
        
        return row;
      });
      
      exportToCSV(statsData, columns, 'production-qualif-stats');
    } else {
      alert('Aucune donnée à exporter');
    }
  };

  const handleExportExcel = () => {
    if (viewMode === 'fiches' && fiches.length > 0) {
      // Préparer les données avec le superviseur
      const exportData = fiches.map(fiche => {
        const agent = agentsData?.find(a => a.id === fiche.id_agent);
        const superviseur = superviseurs.find(s => s.id === agent?.chef_equipe);
        const superviseurName = superviseur?.pseudo || '-';
        
        const { label: displayEtat } = getFicheEtatDisplay(fiche);
        
        return {
          ...fiche,
          superviseur_name: superviseurName,
          etat_titre: displayEtat
        };
      });
      
      const columns = [
        { key: 'date_insert_time', label: 'Date création' },
        { key: 'agent_pseudo', label: 'Agent' },
        { key: 'nom', label: 'Nom' },
        { key: 'prenom', label: 'Prénom' },
        { key: 'tel', label: 'Téléphone' },
        { key: 'cp', label: 'CP' },
        { key: 'etat_titre', label: 'État' },
        ...(canSeeCommentaireQualite ? [{ key: 'commentaire_qualite', label: 'Commentaire Qualité' }] : [])
      ];
      exportToExcel(exportData, columns, 'production-qualif-fiches');
    } else if (viewMode === 'stats' && stats.superviseurs && stats.superviseurs.length > 0) {
      // Exporter les statistiques en format tableau croisé
      // Colonnes : Superviseur + tous les états
      const etatLabels = stats.etats.map(etat => etat.titre || etat.abbreviation);
      
      // Créer les colonnes
      const columns = [
        { key: 'superviseur', label: 'Superviseur' },
        ...etatLabels.map(label => ({ key: `etat_${label}`, label })),
        { key: 'total', label: 'Total' },
        { key: 'etat_Validé', label: 'Validé' }
      ];
      
      // Créer les lignes (une par superviseur)
      const statsData = stats.superviseurs.map(superviseurStat => {
        const superviseurName = superviseurStat.superviseur?.pseudo || 'N/A';
        
        const row = { superviseur: superviseurName };
        
        // Ajouter les valeurs pour chaque état
        stats.etats.forEach(etat => {
          const label = etat.titre || etat.abbreviation;
          row[`etat_${label}`] = superviseurStat.stats[etat.id]?.count || 0;
        });

        row.total = superviseurStat.total || 0;
        // Ajouter la valeur "Validé"
        row['etat_Validé'] = superviseurStat.stats['validated']?.count || 0;
        
        return row;
      });
      
      exportToExcel(statsData, columns, 'production-qualif-stats');
    } else {
      alert('Aucune donnée à exporter');
    }
  };

  const handleExportPDF = () => {
    if (viewMode === 'fiches' && fiches.length > 0) {
      // Préparer les données avec le superviseur et formater les dates
      const exportData = fiches.map(fiche => {
        const agent = agentsData?.find(a => a.id === fiche.id_agent);
        const superviseur = superviseurs.find(s => s.id === agent?.chef_equipe);
        const superviseurName = superviseur?.pseudo || '-';
        
        const { label: displayEtat } = getFicheEtatDisplay(fiche);
        
        return {
          ...fiche,
          superviseur_name: superviseurName,
          date_insert_time: fiche.date_insert_time ? new Date(fiche.date_insert_time).toLocaleDateString('fr-FR') : '-',
          etat_titre: displayEtat
        };
      });
      
      const columns = [
        { key: 'date_insert_time', label: 'Date création' },
        { key: 'agent_pseudo', label: 'Agent' },
        { key: 'nom', label: 'Nom' },
        { key: 'prenom', label: 'Prénom' },
        { key: 'tel', label: 'Téléphone' },
        { key: 'cp', label: 'CP' },
        { key: 'etat_titre', label: 'État' },
        ...(canSeeCommentaireQualite ? [{ key: 'commentaire_qualite', label: 'Commentaire Qualité' }] : [])
      ];
      exportToPDF(exportData, columns, 'production-qualif-fiches', 'Production Qualification - Fiches');
    } else if (viewMode === 'stats' && stats.superviseurs && stats.superviseurs.length > 0) {
      // Exporter les statistiques en format tableau croisé
      // Colonnes : Superviseur + tous les états
      const etatLabels = stats.etats.map(etat => etat.titre || etat.abbreviation);
      
      // Créer les colonnes
      const columns = [
        { key: 'superviseur', label: 'Superviseur' },
        ...etatLabels.map(label => ({ key: `etat_${label}`, label })),
        { key: 'total', label: 'Total' },
        { key: 'etat_Validé', label: 'Validé' }
      ];
      
      // Créer les lignes (une par superviseur)
      const statsData = stats.superviseurs.map(superviseurStat => {
        const superviseurName = superviseurStat.superviseur?.pseudo || 'N/A';
        
        const row = { superviseur: superviseurName };
        
        // Ajouter les valeurs pour chaque état
        stats.etats.forEach(etat => {
          const label = etat.titre || etat.abbreviation;
          row[`etat_${label}`] = superviseurStat.stats[etat.id]?.count || 0;
        });

        row.total = superviseurStat.total || 0;
        // Ajouter la valeur "Validé"
        row['etat_Validé'] = superviseurStat.stats['validated']?.count || 0;
        
        return row;
      });
      
      exportToPDF(statsData, columns, 'production-qualif-stats', 'Production Qualification - Statistiques');
    } else {
      alert('Aucune donnée à exporter');
    }
  };

  const superviseurs = useMemo(() => {
    let list = superviseursData || [];
    if (filters.id_rp) {
      list = list.filter((s) => String(s.id_rp_qualif || '') === String(filters.id_rp));
    }
    return [...list].sort((a, b) =>
      String(a.pseudo || '').localeCompare(String(b.pseudo || ''), 'fr', { sensitivity: 'base' })
    );
  }, [superviseursData, filters.id_rp]);
  const etats = etatsData || [];
  const stats = statsData || { superviseurs: [], etats: [], period: {} };
  const fiches = filteredFiches || [];

  const sortedFiches = useMemo(
    () =>
      sortRowsByConfig(fiches, sortConfig, (row, key) => {
        if (key === 'etat') {
          const { label } = getFicheEtatDisplay(row);
          return label;
        }
        return row[key];
      }),
    [fiches, sortConfig]
  );

  const sortedSuperviseurs = useMemo(
    () =>
      sortRowsByConfig(stats.superviseurs || [], sortConfig, (row, key) => {
        if (key === 'superviseur') {
          const s = row.superviseur || {};
          return s.pseudo || '';
        }
        if (key === 'total') return Number(row.total || 0);
        if (key === 'validated') return Number(row.stats?.validated?.count || 0);
        if (String(key).startsWith('etat_')) {
          const etatId = String(key).replace('etat_', '');
          return Number(row.stats?.[etatId]?.count || 0);
        }
        return '';
      }),
    [stats.superviseurs, sortConfig]
  );

  return (
    <div className="production-qualif">
      <div className="noprint">
        <SystemMessageBanner />
      </div>
      <div className="production-header noprint">
        <h1><FaChartBar /> Production Qualification</h1>
        <div className="header-actions">
          {(canViewStats || canViewFiches || canViewRendementHoraire) && (
            <div className="view-mode-toggle noprint">
              {canViewStats && (
                <button
                  className={`mode-btn ${viewMode === 'stats' ? 'active' : ''}`}
                  onClick={() => setViewMode('stats')}
                >
                  <FaChartBar /> Statistiques
                </button>
              )}
              {canViewFiches && (
                <button
                  className={`mode-btn ${viewMode === 'fiches' ? 'active' : ''}`}
                  onClick={() => setViewMode('fiches')}
                >
                  <FaList /> Fiches
                </button>
              )}
              {canViewRendementHoraire && (
                <button
                  className={`mode-btn ${viewMode === 'rendement' ? 'active' : ''}`}
                  onClick={() => setViewMode('rendement')}
                >
                  <FaClock /> Rendement horaire
                </button>
              )}
            </div>
          )}
          {viewMode !== 'rendement' && (
            <>
              <button 
                className="filter-toggle-btn noprint" 
                onClick={() => setShowFilters(!showFilters)}
              >
                <FaFilter /> {showFilters ? 'Masquer' : 'Afficher'} les filtres
              </button>
              <div className="export-buttons noprint">
                <button className="export-btn" onClick={handleExportExcel} title="Exporter en Excel">
                  <FaFileExcel /> Excel
                </button>
                <button className="export-btn" onClick={handleExportCSV} title="Exporter en CSV">
                  <FaFileCsv /> CSV
                </button>
                <button className="export-btn" onClick={handleExportPDF} title="Exporter en PDF">
                  <FaFilePdf /> PDF
                </button>
              </div>
              <button className="print-btn noprint" onClick={handlePrint}>
                <FaPrint /> Imprimer
              </button>
            </>
          )}
        </div>
      </div>

      {viewMode === 'rendement' && canViewRendementHoraire && (
        <RendementHoraireQualif />
      )}

      {viewMode !== 'rendement' && showFilters && (
        <div className="production-filters noprint">
          <div className="filter-group">
            <label>Date début (heure incluse)</label>
            <input
              type="datetime-local"
              value={toDateTimeLocalValue(filters.date_debut, filters.time_debut)}
              onChange={(e) => handleDateTimeFilterChange('debut', e.target.value)}
            />
          </div>
          <div className="filter-group">
            <label>Date fin (heure incluse)</label>
            <input
              type="datetime-local"
              value={toDateTimeLocalValue(filters.date_fin, filters.time_fin)}
              onChange={(e) => handleDateTimeFilterChange('fin', e.target.value)}
            />
          </div>
          {canFilterByRp && (
            <div className="filter-group">
              <label>RP Qualification</label>
              <select
                value={filters.id_rp}
                onChange={(e) => {
                  const nextRp = e.target.value;
                  setFilters((prev) => ({ ...prev, id_rp: nextRp, id_superviseur: '' }));
                }}
              >
                <option value="">Tous les RP</option>
                {(rpsData || []).map((rp) => (
                  <option key={rp.id} value={rp.id}>
                    {rp.pseudo || `RP #${rp.id}`}
                  </option>
                ))}
              </select>
            </div>
          )}
          <div className="filter-group">
            <label>Superviseur</label>
            <select
              value={filters.id_superviseur}
              onChange={(e) => handleFilterChange('id_superviseur', e.target.value)}
            >
              <option value="">Tous les superviseurs</option>
              {superviseurs.map(superviseur => (
                <option key={superviseur.id} value={superviseur.id}>
                  {superviseur.pseudo || `ID: ${superviseur.id}`}
                </option>
              ))}
            </select>
          </div>
          <div className="filter-group">
            <label>État (multi-select)</label>
            <div className="multi-select-wrapper" ref={multiSelectRef}>
              <div 
                className="multi-select-trigger"
                onClick={() => setIsMultiSelectOpen(!isMultiSelectOpen)}
              >
                <div className="multi-select-selected">
                  {filters.id_etat_final.length === 0 ? (
                    <span className="multi-select-placeholder">Tous les états</span>
                  ) : (
                    <div className="multi-select-badges">
                      {filters.id_etat_final.slice(0, 2).map((etatId, idx) => {
                        if (etatId === 'validated') {
                          return (
                            <span key={idx} className="multi-select-badge">
                              Validée
                            </span>
                          );
                        }
                        if (etatId === 'ko') {
                          return (
                            <span key={idx} className="multi-select-badge ko">
                              KO
                            </span>
                          );
                        }
                        if (etatId === 'hc') {
                          return (
                            <span key={idx} className="multi-select-badge hc">
                              HC
                            </span>
                          );
                        }
                        const etat = etats.find(e => String(e.id) === etatId);
                        return etat ? (
                          <span key={idx} className="multi-select-badge">
                            {etat.titre}
                          </span>
                        ) : null;
                      })}
                      {filters.id_etat_final.length > 2 && (
                        <span className="multi-select-badge more">
                          +{filters.id_etat_final.length - 2}
                        </span>
                      )}
                    </div>
                  )}
                </div>
                <FaChevronDown className={`multi-select-arrow ${isMultiSelectOpen ? 'open' : ''}`} />
              </div>
              {isMultiSelectOpen && (
                <div className="multi-select-dropdown">
                  <div className="multi-select-options">
                    <label className="multi-select-option">
                      <input
                        type="checkbox"
                        checked={filters.id_etat_final.length === 0}
                        onChange={(e) => {
                          if (e.target.checked) {
                            handleFilterChange('id_etat_final', []);
                          }
                        }}
                      />
                      <span>Tous les états</span>
                    </label>
                    <label className="multi-select-option">
                      <input
                        type="checkbox"
                        checked={filters.id_etat_final.includes('validated')}
                        onChange={(e) => {
                          const newEtats = e.target.checked
                            ? [...filters.id_etat_final, 'validated']
                            : filters.id_etat_final.filter(e => e !== 'validated');
                          handleFilterChange('id_etat_final', newEtats);
                        }}
                      />
                      <span>Validée (hors groupe 0, hors KO)</span>
                    </label>
                    <label className="multi-select-option">
                      <input
                        type="checkbox"
                        checked={filters.id_etat_final.includes('ko')}
                        onChange={(e) => {
                          const newEtats = e.target.checked
                            ? [...filters.id_etat_final, 'ko']
                            : filters.id_etat_final.filter((id) => id !== 'ko');
                          handleFilterChange('id_etat_final', newEtats);
                        }}
                      />
                      <span style={{ color: '#dc3545' }}>KO</span>
                    </label>
                    <label className="multi-select-option">
                      <input
                        type="checkbox"
                        checked={filters.id_etat_final.includes('hc')}
                        onChange={(e) => {
                          const newEtats = e.target.checked
                            ? [...filters.id_etat_final, 'hc']
                            : filters.id_etat_final.filter((id) => id !== 'hc');
                          handleFilterChange('id_etat_final', newEtats);
                        }}
                      />
                      <span style={{ color: '#ff9800' }}>HC (hors cible)</span>
                    </label>
                    {etats.filter(e => e.groupe === '0' || e.groupe === 0).map(etat => (
                      <label key={etat.id} className="multi-select-option">
                        <input
                          type="checkbox"
                          checked={filters.id_etat_final.includes(String(etat.id))}
                          onChange={(e) => {
                            const newEtats = e.target.checked
                              ? [...filters.id_etat_final, String(etat.id)]
                              : filters.id_etat_final.filter(e => e !== String(etat.id));
                            handleFilterChange('id_etat_final', newEtats);
                          }}
                        />
                        <span style={{ color: etat.color || '#333' }}>{etat.titre}</span>
                      </label>
                    ))}
                  </div>
                  {filters.id_etat_final.length > 0 && (
                    <div className="multi-select-footer">
                      <button
                        type="button"
                        className="multi-select-clear"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleFilterChange('id_etat_final', []);
                        }}
                      >
                        <FaTimes /> Tout effacer
                      </button>
                      <span className="multi-select-count">
                        {filters.id_etat_final.length} sélectionné{filters.id_etat_final.length > 1 ? 's' : ''}
                      </span>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Recherche rapide pour les fiches */}
      {canViewFiches && viewMode === 'fiches' && (
        <div className="quick-search-container noprint">
          <FaSearch />
          <input
            type="text"
            placeholder="Recherche rapide (nom, prénom, téléphone, code postal, agent, état, commentaire qualité)..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="quick-search-input"
          />
        </div>
      )}

      {viewMode !== 'rendement' && (
      <div className="production-content">
        {viewMode === 'fiches' && canViewFiches ? (
          // Vue fiches
          loadingFiches ? (
            <div className="loading noprint">Chargement des fiches...</div>
          ) : fichesError ? (
            <div className="no-data" style={{ color: 'red' }}>
              Erreur lors du chargement des fiches: {fichesError.message || 'Erreur inconnue'}
              <br />
              <small>Vérifiez que vous avez bien des superviseurs assignés et que les dates sont correctes.</small>
            </div>
          ) : (fichesData && (!fichesData.data || fichesData.data.length === 0)) && !loadingFiches ? (
            <div className="no-data">
              Aucune fiche trouvée pour cette période.
              <br />
              <small>
                Vérifiez vos filtres (dates: {filters.date_debut} - {filters.date_fin}, superviseur, état) ou assurez-vous que des fiches existent pour les agents sous la responsabilité de vos superviseurs.
              </small>
            </div>
          ) : fiches && fiches.length > 0 ? (
            <div className="fiches-table-container">
              <div className="period-info print-period">
                Période : {formatPeriodRange(stats.period || {
                  date_debut: filters.date_debut,
                  date_fin: filters.date_fin,
                  time_debut: filters.time_debut,
                  time_fin: filters.time_fin,
                })}
              </div>
              <div className="results-info noprint">
                {searchTerm || filters.id_superviseur || (filters.id_etat_final && filters.id_etat_final.length > 0) ? (
                  <p>{fiches.length} fiche{fiches.length > 1 ? 's' : ''} trouvée{fiches.length > 1 ? 's' : ''} (sur {fichesData?.data?.length || fichesData?.pagination?.total || 0})</p>
                ) : (
                  <p>Total: {fichesData?.data?.length || fichesData?.pagination?.total || 0} fiche{(fichesData?.data?.length || fichesData?.pagination?.total || 0) > 1 ? 's' : ''}</p>
                )}
              </div>
              <table
                className={`fiches-table fiches-table--wrap${canSeeCommentaireQualite ? '' : ' fiches-table--no-comment'}`}
              >
                <colgroup>
                  <col className="colw-num" />
                  <col className="colw-date" />
                  <col className="colw-agent" />
                  <col className="colw-nom" />
                  <col className="colw-prenom" />
                  <col className="colw-tel" />
                  <col className="colw-cp" />
                  <col className="colw-etat" />
                  {canSeeCommentaireQualite && <col className="colw-comment" />}
                  <col className="colw-actions" />
                </colgroup>
                <thead>
                  <tr>
                    <FicheRowIndexHeader />
                    <th className="col-date fiche-col-date sortable-th" onClick={() => handleSort('date_insert_time')}>
                      Date création {renderSortIcon('date_insert_time')}
                    </th>
                    <th className="col-agent sortable-th" onClick={() => handleSort('agent_pseudo')}>
                      Agent {renderSortIcon('agent_pseudo')}
                    </th>
                    <th className="col-nom sortable-th" onClick={() => handleSort('nom')}>
                      Nom {renderSortIcon('nom')}
                    </th>
                    <th className="col-prenom sortable-th" onClick={() => handleSort('prenom')}>
                      Prénom {renderSortIcon('prenom')}
                    </th>
                    <th className="col-tel sortable-th" onClick={() => handleSort('tel')}>
                      Téléphone {renderSortIcon('tel')}
                    </th>
                    <th className="col-cp sortable-th" onClick={() => handleSort('cp')}>
                      CP {renderSortIcon('cp')}
                    </th>
                    <th className="col-etat sortable-th" onClick={() => handleSort('etat')}>
                      État {renderSortIcon('etat')}
                    </th>
                    {canSeeCommentaireQualite && (
                      <th className="col-comment sortable-th" onClick={() => handleSort('commentaire_qualite')}>
                        Commentaire Qualité {renderSortIcon('commentaire_qualite')}
                      </th>
                    )}
                    <th className="col-actions noprint">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedFiches.map((fiche, rowIndex) => {
                    const { label: displayEtat, color: displayColor } = getFicheEtatDisplay(fiche);
                    return (
                      <tr
                        key={fiche.id || fiche.hash}
                        className={getFicheRowByEtatClassName(displayColor)}
                        style={{ backgroundColor: displayColor }}
                      >
                        <FicheRowIndexCell number={getFicheRowNumber(rowIndex)} />
                        <td className="col-date fiche-col-date">{formatDateTimeFr(fiche.date_insert_time)}</td>
                        <td className="col-agent">{fiche.agent_pseudo || ''}</td>
                        <td className="col-nom fiche-col-nom">{fiche.nom || ''}</td>
                        <td className="col-prenom fiche-col-prenom">{fiche.prenom || ''}</td>
                        <td className="col-tel">{fiche.tel || ''}</td>
                        <td className="col-cp">{fiche.cp || ''}</td>
                        <td className="col-etat etat-col-cell">
                          <span className="etat-text etat-text--wrap">{displayEtat}</span>
                        </td>
                        {canSeeCommentaireQualite && (
                          <td className="col-comment">
                            <div className="comment-quick-edit-container">
                              {canEditCommentaireQualite ? (
                                <>
                                  <div className="comment-quick-actions noprint">
                                    {(() => {
                                      const currentValue = editingComment.hash === fiche.hash ? editingComment.value : (fiche.commentaire_qualite || '');
                                      const originalValue = fiche.commentaire_qualite || '';
                                      const hasChanges = editingComment.hash === fiche.hash && currentValue !== originalValue;
                                      
                                      return hasChanges && (
                                        <>
                                          <button
                                            className="btn-save-comment-quick"
                                            onClick={() => handleSaveComment(fiche.hash)}
                                            disabled={updateCommentaireQualiteMutation.isLoading}
                                            title="Enregistrer (Ctrl+Enter)"
                                          >
                                            <FaSave />
                                          </button>
                                          <button
                                            className="btn-cancel-comment-quick"
                                            onClick={() => {
                                              setEditingComment({ hash: null, value: '' });
                                            }}
                                            disabled={updateCommentaireQualiteMutation.isLoading}
                                            title="Annuler (Echap)"
                                          >
                                            <FaTimes />
                                          </button>
                                        </>
                                      );
                                    })()}
                                  </div>
                                  <textarea
                                    value={editingComment.hash === fiche.hash ? editingComment.value : (fiche.commentaire_qualite || '')}
                                    onChange={(e) => {
                                      if (editingComment.hash !== fiche.hash) {
                                        setEditingComment({ hash: fiche.hash, value: e.target.value });
                                      } else {
                                        setEditingComment({ ...editingComment, value: e.target.value });
                                      }
                                    }}
                                    onFocus={() => {
                                      if (editingComment.hash !== fiche.hash) {
                                        setEditingComment({ hash: fiche.hash, value: fiche.commentaire_qualite || '' });
                                      }
                                    }}
                                    onKeyDown={(e) => handleKeyDown(e, fiche.hash)}
                                    className="comment-textarea-quick"
                                    placeholder="Commentaire qualité... (Ctrl+Enter pour sauvegarder)"
                                    rows={2}
                                  />
                                </>
                              ) : (
                                <div className="comment-readonly">
                                  <div className="comment-readonly-text">
                                    {fiche.commentaire_qualite || <span className="no-comment">Aucun commentaire qualité</span>}
                                  </div>
                                </div>
                              )}
                            </div>
                          </td>
                        )}
                        <td className="col-actions noprint">
                          {isRPQualif ? (
                            <span 
                              className="btn-detail disabled" 
                              title="Accès aux détails désactivé pour les RP Qualification"
                              style={{ 
                                opacity: 0.5, 
                                cursor: 'not-allowed',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                              }}
                            >
                              <FaSearch style={{ color: '#ffffff', fontSize: '13.6px' }} />
                            </span>
                          ) : (
                            <FicheDetailLink 
                              ficheHash={fiche.hash}
                              ficheId={fiche.id}
                              className="btn-detail"
                              title="Voir les détails"
                              style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                            >
                              <FaSearch style={{ color: '#ffffff', fontSize: '13.6px' }} />
                            </FicheDetailLink>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="no-data">Aucune fiche trouvée pour cette période</div>
          )
        ) : loadingStats ? (
          <div className="loading noprint">Chargement des données...</div>
        ) : stats.superviseurs && stats.superviseurs.length > 0 ? (
          <>
            <div className="period-info print-period">
              Période :{' '}
              {stats.period?.start_datetime && stats.period?.end_datetime
                ? formatPeriodRange(stats.period)
                : formatPeriodRange({
                    date_debut: filters.date_debut,
                    date_fin: filters.date_fin,
                    time_debut: filters.time_debut,
                    time_fin: filters.time_fin,
                  })}
            </div>
            <div className="table-container">
              <table className="production-table">
                <thead style={{ backgroundColor: '#9cbfc8', color: '#ffffff' }}>
                  <tr>
                    <th
                      className="sortable-th"
                      style={{ color: '#ffffff' }}
                      onClick={() => handleSort('superviseur')}
                    >
                      Superviseur {renderSortIcon('superviseur')}
                    </th>
                    {stats.etats && stats.etats.map(etat => (
                      <th
                        key={etat.id}
                        title={etat.titre}
                        className="sortable-th"
                        style={{ color: '#ffffff' }}
                        onClick={() => handleSort(`etat_${etat.id}`)}
                      >
                        {etat.abbreviation || etat.titre} {renderSortIcon(`etat_${etat.id}`)}
                      </th>
                    ))}
                    <th
                      className="sortable-th"
                      style={{ color: '#ffffff' }}
                      onClick={() => handleSort('total')}
                    >
                      Total {renderSortIcon('total')}
                    </th>
                    <th
                      className="sortable-th"
                      style={{ color: '#ffffff' }}
                      onClick={() => handleSort('validated')}
                    >
                      Validé {renderSortIcon('validated')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {sortedSuperviseurs.map((superviseurStat, index) => (
                    <tr key={superviseurStat.superviseur.id || index}>
                      <td className="superviseur-cell">
                        <strong>
                          {superviseurStat.superviseur.pseudo || 'N/A'}
                        </strong>
                      </td>
                      {stats.etats && stats.etats.map(etat => {
                        const stat = superviseurStat.stats[etat.id];
                        const count = stat?.count || 0;
                        return (
                          <td 
                            key={etat.id}
                            className="stat-cell"
                            title={etat.titre}
                            style={{ 
                              backgroundColor: count > 0 ? (etat.color ? `${etat.color}20` : '#e3f2fd') : 'transparent',
                              color: count > 0 ? '#333' : '#999'
                            }}
                          >
                            {count}
                          </td>
                        );
                      })}
                      <td className="stat-cell total-inserted" title="Total fiches insérées">
                        <strong>{superviseurStat.total || 0}</strong>
                      </td>
                      <td className="stat-cell validated">
                        {superviseurStat.stats['validated']?.count || 0}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="totals-row">
                    <td><strong>Totaux</strong></td>
                    {stats.etats && stats.etats.map(etat => {
                      const total = stats.superviseurs.reduce((sum, supStat) => {
                        const stat = supStat.stats[etat.id];
                        return sum + (stat?.count || 0);
                      }, 0);
                      return (
                        <td key={etat.id} className="total-cell">
                          <strong>{total}</strong>
                        </td>
                      );
                    })}
                    <td className="total-cell">
                      <strong>
                        {stats.superviseurs.reduce((sum, supStat) =>
                          sum + (Number(supStat.total) || 0), 0
                        )}
                      </strong>
                    </td>
                    <td className="total-cell">
                      <strong>
                        {stats.superviseurs.reduce((sum, supStat) => 
                          sum + (supStat.stats['validated']?.count || 0), 0
                        )}
                      </strong>
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>

            {stats.comparison && (
              <div className="production-comparison noprint">
                <h2 className="production-comparison-title">
                  {stats.comparison.mode === 'previous_month'
                    ? 'Comparatif vs mois précédent (même période)'
                    : 'Comparatif vs veille (même créneau horaire)'}
                </h2>
                <p className="production-comparison-subtitle">
                  Production agents qualification · filtre sur la <strong>date/heure d&apos;insertion</strong>
                  {filters.id_superviseur ? ' · superviseur sélectionné' : ''}
                </p>
                <div className="production-comparison-grid">
                  <div className="comparison-card">
                    <div className="comparison-card-label">Période actuelle</div>
                    <div className="comparison-card-period">{formatPeriodRange(stats.period)}</div>
                    <div className="comparison-metrics">
                      <div className="comparison-metric">
                        <span className="comparison-metric-label">Fiches insérées</span>
                        <strong className="comparison-metric-value">{stats.comparison.current.total}</strong>
                      </div>
                      <div className="comparison-metric">
                        <span className="comparison-metric-label">Performance (conformité)</span>
                        <strong className="comparison-metric-value">{stats.comparison.current.performance}%</strong>
                        <span className="comparison-metric-detail">
                          KO : {stats.comparison.current.nb_ko} ({stats.comparison.current.taux_ko}%)
                          {' · '}
                          HC : {stats.comparison.current.nb_hc} ({stats.comparison.current.taux_hc}%)
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="comparison-card comparison-card-previous">
                    <div className="comparison-card-label">
                      {stats.comparison.mode === 'previous_month'
                        ? 'Mois précédent (même période)'
                        : 'Veille (même créneau)'}
                    </div>
                    <div className="comparison-card-period">
                      {formatPeriodRange(stats.comparison.previous.period)}
                    </div>
                    <div className="comparison-metrics">
                      <div className="comparison-metric">
                        <span className="comparison-metric-label">Fiches insérées</span>
                        <strong className="comparison-metric-value">{stats.comparison.previous.total}</strong>
                      </div>
                      <div className="comparison-metric">
                        <span className="comparison-metric-label">Performance (conformité)</span>
                        <strong className="comparison-metric-value">{stats.comparison.previous.performance}%</strong>
                        <span className="comparison-metric-detail">
                          KO : {stats.comparison.previous.nb_ko} ({stats.comparison.previous.taux_ko}%)
                          {' · '}
                          HC : {stats.comparison.previous.nb_hc} ({stats.comparison.previous.taux_hc}%)
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="comparison-card comparison-card-delta">
                    <div className="comparison-card-label">Évolution</div>
                    <div className="comparison-metrics">
                      <div className="comparison-metric">
                        <span className="comparison-metric-label">Δ Fiches</span>
                        <strong className={`comparison-metric-value ${formatDelta(stats.comparison.delta.total).className}`}>
                          {formatDelta(stats.comparison.delta.total).text}
                        </strong>
                      </div>
                      <div className="comparison-metric">
                        <span className="comparison-metric-label">Δ Performance</span>
                        <strong className={`comparison-metric-value ${formatDelta(stats.comparison.delta.performance, ' pts').className}`}>
                          {formatDelta(stats.comparison.delta.performance, ' pts').text}
                        </strong>
                        <span className="comparison-metric-detail">
                          Δ KO : {formatDelta(stats.comparison.delta.nb_ko).text}
                          {' · '}
                          Δ HC : {formatDelta(stats.comparison.delta.nb_hc).text}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {stats.comparison && stats.comparison_other_plateau && (
              <div className="production-comparison production-comparison-other-plateau noprint">
                <h2 className="production-comparison-title">Comparatif Autre plateau</h2>
                <p className="production-comparison-subtitle">
                  Résultat de l&apos;autre RP sur la même période · chiffres uniquement
                </p>
                <div className="production-comparison-grid">
                  <div className="comparison-card">
                    <div className="comparison-card-label">Mon plateau</div>
                    <div className="comparison-card-period">{formatPeriodRange(stats.period)}</div>
                    <div className="comparison-metrics">
                      <div className="comparison-metric">
                        <span className="comparison-metric-label">Fiches insérées</span>
                        <strong className="comparison-metric-value">{stats.comparison.current.total}</strong>
                      </div>
                      <div className="comparison-metric">
                        <span className="comparison-metric-label">Performance (conformité)</span>
                        <strong className="comparison-metric-value">{stats.comparison.current.performance}%</strong>
                        <span className="comparison-metric-detail">
                          KO : {stats.comparison.current.nb_ko} ({stats.comparison.current.taux_ko}%)
                          {' · '}
                          HC : {stats.comparison.current.nb_hc} ({stats.comparison.current.taux_hc}%)
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="comparison-card comparison-card-other">
                    <div className="comparison-card-label">Autre plateau</div>
                    <div className="comparison-card-period">{formatPeriodRange(stats.period)}</div>
                    <div className="comparison-metrics">
                      <div className="comparison-metric">
                        <span className="comparison-metric-label">Fiches insérées</span>
                        <strong className="comparison-metric-value">{stats.comparison_other_plateau.other.total}</strong>
                      </div>
                      <div className="comparison-metric">
                        <span className="comparison-metric-label">Performance (conformité)</span>
                        <strong className="comparison-metric-value">{stats.comparison_other_plateau.other.performance}%</strong>
                        <span className="comparison-metric-detail">
                          KO : {stats.comparison_other_plateau.other.nb_ko} ({stats.comparison_other_plateau.other.taux_ko}%)
                          {' · '}
                          HC : {stats.comparison_other_plateau.other.nb_hc} ({stats.comparison_other_plateau.other.taux_hc}%)
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="comparison-card comparison-card-delta">
                    <div className="comparison-card-label">Écart vs autre plateau</div>
                    <div className="comparison-metrics">
                      <div className="comparison-metric">
                        <span className="comparison-metric-label">Δ Fiches</span>
                        <strong className={`comparison-metric-value ${formatDelta(stats.comparison_other_plateau.delta.total).className}`}>
                          {formatDelta(stats.comparison_other_plateau.delta.total).text}
                        </strong>
                      </div>
                      <div className="comparison-metric">
                        <span className="comparison-metric-label">Δ Performance</span>
                        <strong className={`comparison-metric-value ${formatDelta(stats.comparison_other_plateau.delta.performance, ' pts').className}`}>
                          {formatDelta(stats.comparison_other_plateau.delta.performance, ' pts').text}
                        </strong>
                        <span className="comparison-metric-detail">
                          Δ KO : {formatDelta(stats.comparison_other_plateau.delta.nb_ko).text}
                          {' · '}
                          Δ HC : {formatDelta(stats.comparison_other_plateau.delta.nb_hc).text}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="no-data">Aucune donnée disponible pour cette période</div>
        )}
      </div>
      )}
    </div>
  );
};

export default ProductionQualif;

